import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { axiosHelper, tokenHelper } from '../helpers';
import { useAuth } from '../Auth/Auth';
import { useUsageStore } from '../Usage';


export const useExperience = create(
    persist(
        (set, get) => ({
            // Local state
            currentStep: 1,
            businessType: null,
            selectedSiteId: null,
            plan: 'pro',
            siteInfo: null,
            branding: {
                primaryColor: '#c59253',
                logo: null
            },
            menu: {
                currency: 'QAR',
                sections: []
            },
            spaces: [],
            splash: {
                enabled: false
            },
            routing: null,
            experienceMode: 'menu-first', // 'menu-first' or 'space-first'
            getTerminology: () => {
                const type = get().businessType?.toLowerCase();
                const terms = {
                    restaurant: { space: 'Table', spacePlural: 'Tables', business: "Restaurant" },
                    office: { space: 'Desk', spacePlural: 'Desks', business: "Office" },
                    beach: { space: 'Umbrella', spacePlural: 'Umbrellas', business: "Beach" }
                };
                return terms[type] || { space: 'Space', spacePlural: 'Spaces' };
            },

            // UI state
            loading: false,
            error: null,
            showMobilePreview: false,
            submissionProgress: null,
            submissionResult: null,

            fetchSubmissionResult: async (siteId) => {
                if (!siteId) return;
                set({ loading: true, submissionProgress: 'Recovering session...' });
                try {
                    const accessToken = await tokenHelper.getToken();
                    const headers = { Authorization: `Bearer ${accessToken}` };
                    const fetchParams = new URLSearchParams({ page: 1, PerPage: 100 });

                    // Fetch the essentials
                    const [spaceResponse, qrResponse, menuResponse, kitchensResponse] = await Promise.all([
                        axiosHelper.get(`/v2/site/${siteId}/spaces?${fetchParams.toString()}`, { headers }),
                        axiosHelper.get(`/qr/site/${siteId}`, { headers }),
                        axiosHelper.get("/menu/links/a", { headers }),
                        axiosHelper.get(`/kitchen/site/${siteId}`, { headers })
                    ]);

                    // Resolve kitchen and token
                    const kitchenId = spaceResponse?.spaces?.[0]?.kitchen_id;
                    let kitchenToken = null;

                    if (kitchenId && kitchensResponse?.kitchens) {
                        const matchingKitchen = kitchensResponse.kitchens.find(k => k.kitchen_id === kitchenId);
                        kitchenToken = matchingKitchen?.token || null;
                    }

                    const spacesData = spaceResponse?.spaces || [];
                    const qrData = Array.isArray(qrResponse) ? qrResponse : [];
                    const menuData = Array.isArray(menuResponse) ? menuResponse : [];

                    const finalSpaces = spacesData.map((space) => {
                        const matchingMenu = menuData.find(m => m?.space_id === space?.space_id);
                        const matchingQR = qrData.find(qr => qr?.space_id === space?.space_id);

                        return {
                            ...space,
                            id: space.space_id,
                            ...(matchingMenu || {}),
                            qrKey: matchingQR?.key || matchingMenu?.encrypted || null,
                        };
                    });

                    const finalResult = {
                        site_id: siteId,
                        kitchen_id: kitchenId,
                        kitchen_token: kitchenToken,
                        space_info: finalSpaces
                    };

                    set({ submissionResult: finalResult, loading: false });
                    return finalResult;
                } catch (error) {
                    console.error('Error recovery failed:', error);
                    set({ loading: false });
                }
            },

            // Actions
            setCurrentStep: async (step) => {
                const state = get();
                // If moving to Menu Builder (usually step 3) for the first time and menu is empty
                if (step === 3 && state.menu?.sections?.length === 0) {
                    const defaultSection = {
                        id: 'default-section',
                        name: 'Breakfast',
                        name_ar: 'الفطور',
                        description: 'Best Breakfast in Town!',
                        description_ar: 'الفطور الأفضل في المدينة!',
                        items: [{
                            id: 'default-item',
                            name: 'Espresso',
                            nameAr: 'اسبريسو',
                            description: 'Rich, full-bodied espresso with a velvety crema.',
                            descriptionAr: 'اسبريسو غني وقوي مع كريمة مخملية.',
                            imagePreview: "https://res.cloudinary.com/dm2k40ije/image/upload/v1733862442/ku1hti4rhk6kj7kyjgx7.png",
                            price: 5,
                            available: true,
                            tags: [],
                            IsListed: true,
                            type: get().businessType?.toUpperCase() === 'BEACH' ? 'SERVICE' : "DEFAULT",
                            options: [
                                {
                                    id: null,
                                    name: 'Preferences',
                                    nameAr: 'التفضيلات',
                                    choices: [
                                        { name: 'Normal', price: 0, isAvailable: true },
                                        { name: 'Large', price: 5, isAvailable: true },
                                        { name: 'Oat Milk', price: 4, isAvailable: true }
                                    ]
                                }
                            ]
                        }]
                    };
                    set({ menu: { ...state.menu, sections: [defaultSection] } });
                }
                set({ currentStep: step });

                // Sync with backend if logged in
                try {
                    const accessToken = await tokenHelper.getToken();
                    if (accessToken) {

                        await axiosHelper.patch('/user/onboarding/step', { step }, {
                            headers: { Authorization: `Bearer ${accessToken}` }
                        });
                    }
                } catch (e) {
                    console.warn("Failed to sync onboarding step", e);
                }
            },

            setBusinessType: (type) => set({ businessType: type }),
            setSelectedSiteId: (id) => set({ selectedSiteId: id }),
            setPlan: (plan) => set({ plan }),
            setSiteInfo: (info) => set({ siteInfo: { currency: 'QAR', ...info } }),
            setBranding: (branding) => set((state) => ({ branding: { ...state.branding, ...branding } })),

            // Complex updaters
            updateMenu: (updates) => set((state) => ({ menu: { ...state.menu, ...updates } })),
            updateSpaces: (spaces) => set({ spaces }),
            updateSplash: (splash) => set((state) => ({ splash: { ...state.splash, ...splash } })),
            updateRouting: (routing) => set({ routing }),
            setExperienceMode: (experienceMode) => set({ experienceMode }),

            toggleMobilePreview: () => set((state) => ({ showMobilePreview: !state.showMobilePreview })),

            // API Interactions - Sequential submission to individual endpoints
            submitExperience: async () => {
                set({ loading: true, error: null, submissionProgress: 'Validating plan limits...' });
                const accessToken = await tokenHelper.getToken();
                const state = get();
                const headers = { Authorization: `Bearer ${accessToken}` };

                // ====== PRE-CHECK: Plan Limits ======
                try {
                    const { getLimitInfo, fetchUsage } = useUsageStore.getState();
                    const usage = await fetchUsage(); // Get latest
                    if (!usage) throw new Error("Failed to verify your account limits. Please try again.");

                    const sections = state.menu?.sections || [];
                    const totalItems = sections.reduce((acc, section) => acc + (section.items?.length || 0), 0);
                    const totalSpaces = state.spaces?.filter(s => !s.parentId).length || 1;

                    const siteLimit = getLimitInfo("sites");
                    const menuLimit = getLimitInfo("menus");
                    const itemLimit = getLimitInfo("menu_items");
                    const spaceLimit = getLimitInfo("spaces");
                    const stationLimit = getLimitInfo("stations");

                    const errors = [];
                    // Check if site creation is needed (if no selectedSiteId)
                    if (!state.selectedSiteId && !siteLimit.isUnlimited && siteLimit.remaining < 1) {
                        errors.push("You have reached the maximum number of restaurants for your plan.");
                    }
                    if (!menuLimit.isUnlimited && menuLimit.remaining < sections.length) {
                        errors.push(`Menu sections limit reached (Needed: ${sections.length}, Available: ${menuLimit.remaining})`);
                    }
                    if (!itemLimit.isUnlimited && itemLimit.remaining < totalItems) {
                        errors.push(`Menu items limit reached (Needed: ${totalItems}, Available: ${itemLimit.remaining})`);
                    }
                    if (!spaceLimit.isUnlimited && spaceLimit.remaining < totalSpaces) {
                        errors.push(`Spaces limit reached (Needed: ${totalSpaces}, Available: ${spaceLimit.remaining})`);
                    }

                    if (errors.length > 0) {
                        const errorMsg = errors[0]; // Show first error for simplicity
                        set({ error: errorMsg, loading: false, submissionProgress: 'Cancelled' });
                        throw new Error(errorMsg);
                    }
                } catch (e) {
                    if (e.message.includes("limit reached") || e.message.includes("maximum") || e.message.includes("failed to verify")) throw e;
                    console.warn("Limit check skipped or encountered error", e);
                }

                // Track created IDs for linking and CLEANUP on failure
                const createdIds = {
                    site_id: null,
                    isNewSite: false,
                    kitchen_id: null,
                    isNewKitchen: false,
                    kitchen_token: null,
                    menu_ids: [], // { frontendId, backendId }
                    item_ids: [], // { frontendId, backendId }
                    space_ids: [], // { frontendId, backendId }
                    splash_id: null,
                };

                const cleanup = async () => {
                    console.log("Cleaning up created resources due to failure...");
                    try {
                        // Delete in reverse order
                        if (createdIds.splash_id) await axiosHelper.delete(`/splash/${createdIds.splash_id}`, { headers }).catch(e => console.warn("Cleanup Splash failed", e));

                        for (const space of createdIds.space_ids) {
                            await axiosHelper.delete(`/v2/space/${space.backendId}`, { headers }).catch(e => console.warn("Cleanup Space failed", e));
                        }

                        for (const item of createdIds.item_ids) {
                            await axiosHelper.delete(`/menu-item/${item.backendId}`, { headers }).catch(e => console.warn("Cleanup Item failed", e));
                        }

                        for (const menu of createdIds.menu_ids) {
                            await axiosHelper.delete(`/menu/${menu.backendId}`, { headers }).catch(e => console.warn("Cleanup Menu failed", e));
                        }

                        if (createdIds.isNewKitchen && createdIds.kitchen_id) {
                            await axiosHelper.delete(`/kitchen/${createdIds.kitchen_id}`, { headers }).catch(e => console.warn("Cleanup Kitchen failed", e));
                        }

                        if (createdIds.isNewSite && createdIds.site_id) {
                            await axiosHelper.delete(`/site/${createdIds.site_id}`, { headers }).catch(e => console.warn("Cleanup Site failed", e));
                        }
                    } catch (e) {
                        console.error("Critical cleanup failure", e);
                    }
                };

                try {
                    // ====== STEP 1: Create Site OR Use Existing ======
                    if (state.selectedSiteId) {
                        set({ submissionProgress: 'Using existing site...' });
                        createdIds.site_id = state.selectedSiteId;
                    } else {
                        set({ submissionProgress: 'Creating site...' });
                        const siteFormData = new FormData();
                        siteFormData.append("name", state.siteInfo?.businessName || "");
                        siteFormData.append("name_ar", state.siteInfo?.name_ar || state.siteInfo?.nameAr || "");
                        siteFormData.append("address", state.siteInfo?.address || "");
                        siteFormData.append("address_ar", state.siteInfo?.address_ar || state.siteInfo?.addressAr || "");
                        siteFormData.append("phone", state.siteInfo?.phone || "");
                        siteFormData.append("latitude", parseFloat(state.siteInfo?.latitude) || 0);
                        siteFormData.append("longitude", parseFloat(state.siteInfo?.longitude) || 0);
                        siteFormData.append("file", state.siteInfo?.logo || "");

                        const siteResponse = await axiosHelper.post('/site/create', siteFormData, { headers });
                        createdIds.site_id = siteResponse.site_id;
                        createdIds.isNewSite = true;

                        // Link site to user
                        await axiosHelper.patch(`/user/link-site?siteId=${createdIds.site_id}`, {}, { headers });
                    }

                    // ====== STEP 2: Create Kitchen (Skip for menu-first) OR Use Existing ======
                    set({ submissionProgress: 'Configuring station...' });

                    // Check for existing kitchen first if we are using existing site
                    let existingKitchen = null;
                    if (state.selectedSiteId) {
                        const kitchensResponse = await axiosHelper.get(`/kitchen/site/${state.selectedSiteId}`, { headers });
                        if (kitchensResponse?.kitchens?.length > 0) {
                            existingKitchen = kitchensResponse.kitchens[0];
                        }
                    }

                    if (existingKitchen) {
                        createdIds.kitchen_id = existingKitchen.kitchen_id;
                        createdIds.kitchen_token = existingKitchen.token;
                    } else {
                        const kitchenResponse = await axiosHelper.post('/kitchen/create', {
                            name: 'Main Kitchen',
                            site_id: createdIds.site_id,
                        }, { headers });
                        createdIds.kitchen_id = kitchenResponse.kitchen.kitchen_id;
                        createdIds.kitchen_token = kitchenResponse.token;
                        createdIds.isNewKitchen = true;
                    }


                    // ====== STEP 3: Create Menus (Sections) ======
                    set({ submissionProgress: 'Creating menus...' });
                    for (const section of (state.menu?.sections || [])) {
                        const menuResponse = await axiosHelper.post('/menu/create', {
                            name: section.name,
                            name_ar: section.name_ar || section.nameAr || null,
                            description: section.description || null,
                            description_ar: section.description_ar || section.descriptionAr || null,
                            site_id: createdIds.site_id,
                            currency: state.menu?.currency || 'QAR',
                        }, { headers });
                        createdIds.menu_ids.push({
                            frontendId: section.id,
                            backendId: menuResponse.menu_id,
                        });

                        // ====== STEP 4: Create Menu Items for this section ======
                        set({ submissionProgress: `Creating items for ${section.name}...` });
                        for (const item of (section.items || [])) {
                            const itemFormData = new FormData();
                            itemFormData.append("title", item.name || "");
                            itemFormData.append("title_ar", item.nameAr || "");
                            itemFormData.append("description", item.description || "");
                            itemFormData.append("description_ar", item.descriptionAr || "");
                            itemFormData.append("price", parseInt(item.price) || 0);
                            itemFormData.append("promo_price", parseInt(item.promo_price) || 0);
                            itemFormData.append("is_promo", (item.is_promo || false).toString());
                            itemFormData.append("available", (item.available !== false).toString());
                            itemFormData.append("isHidden", (item.isHidden || false).toString());
                            itemFormData.append("IsListed", (item.IsListed !== false).toString());
                            itemFormData.append("menu_id", parseInt(menuResponse.menu_id));
                            itemFormData.append("type", item.type?.toUpperCase() || 'DEFAULT');

                            if (item.type === "AD" && item.adUrl) {
                                itemFormData.append("ad_url", item.adUrl);
                            }

                            // Handle images
                            if (item.imageFile) {
                                itemFormData.append("item_images", item.imageFile);
                            } else {
                                // If no custom file, check for catalog image or image_url
                                const catalogImage = item.catalogImg || (typeof item.image_url === 'string' ? item.image_url : null);
                                if (catalogImage && catalogImage !== "") {
                                    itemFormData.append("catalog_img", catalogImage);
                                }
                            }

                            (item.tags || []).forEach((tag) => {
                                itemFormData.append("tags", tag);
                            });

                            // Legacy check, though usually handled above
                            if (item.catalogImg && item.catalogImg !== "" && !itemFormData.has("catalog_img")) {
                                itemFormData.append("catalog_img", item.catalogImg);
                            }

                            const itemResponse = await axiosHelper.post('/menu-item', itemFormData, { headers });
                            createdIds.item_ids.push({
                                frontendId: item.id,
                                backendId: itemResponse.menu_item_id
                            });

                            // ====== STEP 4b: Create Item Options ======
                            if (item.options && Array.isArray(item.options) && item.options.length > 0) {
                                for (const opt of item.options) {
                                    // Skip empty option groups
                                    if (!opt.name && (!opt.choices || opt.choices.length === 0)) continue;

                                    await axiosHelper.post(`/menu-item/${itemResponse.menu_item_id}/options`, {
                                        name: opt.name || 'Choices',
                                        name_ar: opt.nameAr || opt.name_ar || null,
                                        type: opt.id ? 'standard' : 'custom',
                                        existing_option_id: opt.id || null,
                                        choices: (opt.choices || []).map(c => ({
                                            name: c.name,
                                            name_ar: c.nameAr || c.name_ar || null,
                                            price: parseFloat(c.price) || 0,
                                            available: c.isAvailable !== false
                                        }))
                                    }, { headers });
                                }
                            }
                        }
                    }

                    // ====== STEP 5: Create Spaces ======
                    set({ submissionProgress: 'Checking spaces...' });
                    let spacesToCreate = [...(state.spaces || [])];
                    let findExistingSpaces = [];

                    // Check for existing spaces if using existing site
                    if (state.selectedSiteId) {
                        const fetchParams = new URLSearchParams({ page: 1, PerPage: 100 });
                        const existingSpacesResponse = await axiosHelper.get(`/v2/site/${state.selectedSiteId}/spaces?${fetchParams.toString()}`, { headers });
                        if (existingSpacesResponse?.spaces?.length > 0) {
                            findExistingSpaces = existingSpacesResponse.spaces;
                        }
                    }

                    // Experience Split Logic: If we are in menu-first and no spaces exist, create a default one
                    // ONLY if we didn't find any existing spaces
                    if (state.experienceMode === 'menu-first' && spacesToCreate.length === 0 && findExistingSpaces.length === 0) {
                        const terminology = state.getTerminology();
                        spacesToCreate = [{
                            call_active: false,
                            name: terminology.space + " 1",
                            nameAr: 'الصالة الرئيسية',
                            selectedSections: state.menu?.sections?.map(s => s.id) || []
                        }];
                        // Update local state too so UI can reflect or recover
                        set({ spaces: spacesToCreate });
                    }

                    const allMenuIds = createdIds.menu_ids.map(m => m.backendId);

                    for (const space of spacesToCreate) {
                        // Skip subspaces - they're handled separately via parent
                        if (space.parentId) continue;

                        // Determine service_level based on routing config
                        // Default: Disable ordering for menu-first (Quick Setup), Enable for space-first (Advanced)
                        let serviceLevel = state.experienceMode === 'menu-first' ? 0 : 1;

                        if (state.routing) {
                            if (state.routing.enabled === false) {
                                serviceLevel = 0;
                            } else if (state.routing.mode === 'specific') {
                                serviceLevel = (state.routing.enabledSpaces || []).includes(space.id) ? 1 : 0;
                            }
                        }

                        // Map frontend section IDs to backend menu IDs using sectionOrder for proper ordering
                        const orderedSectionIds = (space.sectionOrder && space.sectionOrder.length > 0)
                            ? space.sectionOrder
                            : (space.selectedSections || []);

                        const spaceMenuIds = orderedSectionIds
                            .map(sectionId => createdIds.menu_ids.find(m => m.frontendId === sectionId)?.backendId)
                            .filter(id => id !== undefined);

                        // CRITICAL FIX: Ensure menu_ids is never empty if we have menus
                        const finalMenuIds = spaceMenuIds.length > 0 ? spaceMenuIds : allMenuIds;

                        const spaceResponse = await axiosHelper.post('/v2/space/create', {
                            name: space.name,
                            name_ar: space.nameAr || null,
                            site_id: createdIds.site_id,
                            kitchen_id: createdIds.kitchen_id || null,
                            menu_ids: finalMenuIds,
                            service_level: serviceLevel,
                            type: state.businessType?.toUpperCase() === 'RESTAURANT' ? 'RESTAURANT' :
                                state.businessType?.toUpperCase() === 'OFFICE' ? 'OFFICE' :
                                    state.businessType?.toUpperCase() === 'BEACH' ? 'BEACH' : 'MAJLISS',
                            default_lang: 'EN',
                            showFeedback: true,
                            call_active: false,
                            saveLastOrder: false
                        }, { headers });

                        createdIds.space_ids.push({
                            frontendId: space.id,
                            backendId: spaceResponse.space_id,
                        });

                        // Create QR Code for the parent space
                        await axiosHelper.post('/qr/create', {
                            kitchen_id: createdIds.kitchen_id || null,
                            space_id: spaceResponse.space_id,
                            menu_ids: finalMenuIds,
                        }, { headers });

                        // ====== STEP 5b: Create SubSpaces if any (children of this parent) ======
                        const childSpaces = (state.spaces || []).filter(s => s.parentId === space.id);
                        if (childSpaces.length > 0) {
                            set({ submissionProgress: `Creating subspaces for ${space.name}...` });
                            await axiosHelper.post('/sub-space/create-many', {
                                subSpaces: childSpaces.map(sub => ({
                                    name: sub.name,
                                    space_id: spaceResponse.space_id
                                })),
                                kitchen_id: createdIds.kitchen_id || null,
                                menu_ids: finalMenuIds, // SubSpaces inherit parent's menus
                            }, { headers });
                        }
                    }

                    // ====== STEP 5c: Fetch all spaces to get QR keys (Strictly matching space.js logic) ======
                    set({ submissionProgress: 'Fetching QR codes...' });
                    const fetchToken = await tokenHelper.getToken();
                    const fetchParams = new URLSearchParams({ page: 1, PerPage: 100 });

                    const [srvSpaceResponse, srvQrResponse, srvMenuResponse] = await Promise.all([
                        axiosHelper.get(`/v2/site/${createdIds.site_id}/spaces?${fetchParams.toString()}`, {
                            headers: { Authorization: `Bearer ${fetchToken}` }
                        }),
                        axiosHelper.get(`/qr/site/${createdIds.site_id}`, {
                            headers: { Authorization: `Bearer ${fetchToken}` }
                        }),
                        axiosHelper.get("/menu/links/a", {
                            headers: { Authorization: `Bearer ${fetchToken}` }
                        }),
                    ]);

                    const spacesData = srvSpaceResponse?.spaces || [];
                    const qrData = Array.isArray(srvQrResponse) ? srvQrResponse : [];
                    const menuData = Array.isArray(srvMenuResponse) ? srvMenuResponse : [];

                    const finalSpaces = spacesData.map((space) => {
                        const matchingMenu = menuData.find(m => m?.space_id === space?.space_id);
                        const matchingQR = qrData.find(qr => qr?.space_id === space?.space_id);

                        return {
                            ...space,
                            id: space.space_id, // Explicit ID for UI loops
                            ...(matchingMenu || {}),
                            qrKey: matchingQR?.key || matchingMenu?.encrypted || null,
                        };
                    });
                    if (createdIds.kitchen_id) {
                        // ====== STEP 6: Update Kitchen with space_list and menu_list ======
                        set({ submissionProgress: 'Linking everything...' });

                        let combinedSpaceIds = createdIds.space_ids.map(s => s.backendId);
                        let combinedMenuIds = createdIds.menu_ids.map(m => m.backendId);

                        if (state.selectedSiteId && createdIds.kitchen_id) {
                            try {
                                const siteKitchensRes = await axiosHelper.get(`/kitchen/site/${createdIds.site_id}`, { headers });
                                const thisKitchen = siteKitchensRes?.kitchens?.find(k => k.kitchen_id === createdIds.kitchen_id);

                                if (thisKitchen) {
                                    const currKitchenSpaces = thisKitchen.space_list || [];
                                    const currKitchenMenus = thisKitchen.menu_list || [];
                                    combinedSpaceIds = [...new Set([...currKitchenSpaces, ...combinedSpaceIds])];
                                    combinedMenuIds = [...new Set([...currKitchenMenus, ...combinedMenuIds])];
                                }
                            } catch (e) {
                                console.warn("Failed to fetch existing kitchen details, appending blindly might overwrite", e);
                            }
                        }

                        await axiosHelper.patch(`/kitchen/update/${createdIds.kitchen_id}`, {
                            space_list: combinedSpaceIds,
                            menu_list: combinedMenuIds,
                        }, { headers });
                    }
                    // ====== STEP 7: Create Splash (if enabled) ======
                    if (state.splash?.enabled) {
                        set({ submissionProgress: 'Creating splash screen...' });

                        // Resolve backend ID for the service item if selected
                        let menuItemId = null;
                        if (state.splash.menu_item_id) {
                            menuItemId = createdIds.item_ids.find(i => i.frontendId === state.splash.menu_item_id)?.backendId;
                        }

                        const splashResponse = await axiosHelper.post('/splash', {
                            title: state.splash.title || 'Welcome',
                            subtitle: state.splash.subtitle || '',
                            menu_button_caption: state.splash.menu_button_caption || 'View Menu',
                            action__button_text: state.splash.action__button_text || '',
                            menu_url: state.splash.menu_url || null,
                            is_active: true,
                            menu_item_id: menuItemId,
                            space_ids: createdIds.space_ids.map(s => s.backendId),
                        }, { headers });

                        createdIds.splash_id = splashResponse?.id || splashResponse?.splash_id;
                    }

                    // ====== STEP 8: Mark Onboarding as Complete ======
                    set({ submissionProgress: 'Finalizing onboarding...' });
                    await axiosHelper.patch('/user/onboarding/complete', {}, { headers });
                    await useAuth.getState().refreshUser();


                    // Success!
                    const submissionResultObj = {
                        site_id: createdIds.site_id,
                        kitchen_id: createdIds.kitchen_id,
                        kitchen_token: createdIds.kitchen_token,
                        space_ids: createdIds.space_ids,
                        space_info: finalSpaces
                    };

                    set({
                        loading: false,
                        submissionProgress: 'Complete!',
                        submissionResult: submissionResultObj,
                    });

                    return {
                        success: true,
                        ...submissionResultObj
                    };
                } catch (error) {
                    console.error('Error submitting experience, rolling back...', error);

                    set({ submissionProgress: 'Rolling back changes...' });
                    await cleanup();

                    set({
                        error: error.response?.data?.message || error.message || 'Failed to submit experience',
                        loading: false,
                        submissionProgress: 'Failed',
                    });
                    throw error;
                }
            },

            submitSpaces: async (payloadList) => {
                set({ loading: true, error: null, submissionProgress: 'Validating plan limits...' });
                const accessToken = await tokenHelper.getToken();
                const state = get();
                const headers = { Authorization: `Bearer ${accessToken}` };

                // ====== PRE-CHECK: Plan Limits ======
                try {
                    const { getLimitInfo, fetchUsage } = useUsageStore.getState();
                    const usage = await fetchUsage(); // Get latest
                    if (!usage) throw new Error("Failed to verify your account limits. Please try again.");

                    const spaceLimit = getLimitInfo("spaces");
                    const newSpacesToCreate = payloadList.filter(item => !item.isExisting).length;

                    if (!spaceLimit.isUnlimited && spaceLimit.remaining < newSpacesToCreate) {
                        const errorMsg = `Spaces limit reached. You can create ${spaceLimit.remaining} more space(s), but you requested ${newSpacesToCreate}.`;
                        set({ error: errorMsg, loading: false, submissionProgress: 'Cancelled' });
                        throw new Error(errorMsg);
                    }
                } catch (e) {
                    if (e.message.includes("limit reached") || e.message.includes("failed to verify")) throw e;
                    console.warn("Limit check skipped or encountered error", e);
                }

                // Track newly created resources for ROLLBACK
                const newlyCreatedSpaceIds = [];
                const newlyDuplicatedMenuIds = [];

                const cleanup = async () => {
                    try {
                        for (const spaceId of newlyCreatedSpaceIds) {
                            await axiosHelper.delete(`/v2/space/${spaceId}`, { headers }).catch(e => console.warn("Space cleanup failed", e));
                        }
                        for (const menuId of newlyDuplicatedMenuIds) {
                            await axiosHelper.delete(`/menu/${menuId}`, { headers }).catch(e => console.warn("Menu cleanup failed", e));
                        }
                    } catch (e) {
                        console.error("Critical cleanup failure in submitSpaces", e);
                    }
                };

                try {
                    const processedSpaces = []; // To track for kitchen/UI
                    const affectedKitchens = new Set();
                    const affectedSites = new Set();

                    // ====== STEP 0: Handle Cross-Site Menu Duplication ======
                    set({ submissionProgress: 'Preparing menus...' });
                    const menuCache = {}; // "siteId_originalMenuId" -> newMenuId

                    for (const item of payloadList) {
                        if (!item.menuDetails || item.menuDetails.length === 0) continue;

                        const resolvedMenuIds = [];
                        for (const menu of item.menuDetails) {
                            if (menu.sourceSiteId && menu.sourceSiteId !== item.siteId) {
                                const cacheKey = `${item.siteId}_${menu.id}`;
                                if (menuCache[cacheKey]) {
                                    resolvedMenuIds.push(menuCache[cacheKey]);
                                } else {
                                    set({ submissionProgress: `Duplicating menu for ${item.name}...` });
                                    const dupRes = await axiosHelper.post(`/menu/duplicate/${menu.id}`, {
                                        site_id: item.siteId
                                    }, { headers });
                                    menuCache[cacheKey] = dupRes.menu_id;
                                    newlyDuplicatedMenuIds.push(dupRes.menu_id);
                                    resolvedMenuIds.push(dupRes.menu_id);
                                }
                            } else {
                                resolvedMenuIds.push(menu.id);
                            }
                        }
                        item.menuIds = resolvedMenuIds;
                    }

                    // ====== STEP 1: Create NEW Spaces & Update EXISTING Spaces ======
                    set({ submissionProgress: 'Processing spaces...' });
                    for (const item of payloadList) {
                        if (item.isExisting && item.spaceId) {
                            // ---- UPDATE existing space (Note: if update fails, we don't necessarily rollback previous creations? 
                            // Actually, if ONE fails, we rollback EVERYTHING new from THIS submission)
                            set({ submissionProgress: `Check Space ${item.name}...` });

                            processedSpaces.push({
                                siteId: item.siteId,
                                kitchenId: item.kitchenId,
                                spaceId: item.spaceId,
                                menuIds: item.menuIds
                            });

                            if (item.kitchenId) affectedKitchens.add(item.kitchenId);
                            affectedSites.add(item.siteId);
                        } else {
                            // ---- CREATE new space ----
                            set({ submissionProgress: `Creating ${item.name}...` });
                            const spaceResponse = await axiosHelper.post('/v2/space/create', {
                                name: item.name,
                                name_ar: item.nameAr || null,
                                site_id: item.siteId,
                                kitchen_id: item.kitchenId || null,
                                menu_ids: item.menuIds || [],
                                service_level: 1,
                                type: state.businessType?.toUpperCase() === 'RESTAURANT' ? 'RESTAURANT' :
                                    state.businessType?.toUpperCase() === 'OFFICE' ? 'OFFICE' :
                                        state.businessType?.toUpperCase() === 'BEACH' ? 'BEACH' : 'MAJLISS',
                                default_lang: 'EN',
                                showFeedback: true,
                                call_active: true,
                                saveLastOrder: false
                            }, { headers });

                            newlyCreatedSpaceIds.push(spaceResponse.space_id);
                            processedSpaces.push({
                                siteId: item.siteId,
                                kitchenId: item.kitchenId,
                                spaceId: spaceResponse.space_id,
                                menuIds: item.menuIds
                            });

                            if (item.kitchenId) affectedKitchens.add(item.kitchenId);
                            affectedSites.add(item.siteId);

                            // Create QR Code for NEW space only
                            await axiosHelper.post('/qr/create', {
                                kitchen_id: item.kitchenId || null,
                                space_id: spaceResponse.space_id,
                                menu_ids: item.menuIds || [],
                            }, { headers });
                        }
                    }

                    // ====== STEP 2: Update Kitchens (Link new spaces/menus) ======
                    for (const kitchenId of affectedKitchens) {
                        set({ submissionProgress: `Linking kitchen...` });
                        const kitchenSpaces = processedSpaces.filter(s => s.kitchenId === kitchenId).map(s => s.spaceId);
                        const kitchenMenus = [...new Set(processedSpaces.filter(s => s.kitchenId === kitchenId).flatMap(s => s.menuIds))];

                        try {
                            let currentSpaces = [];
                            let currentMenus = [];
                            const siteId = processedSpaces.find(s => s.kitchenId === kitchenId)?.siteId;
                            if (siteId) {
                                const siteKitchensRes = await axiosHelper.get(`/kitchen/site/${siteId}`, { headers });
                                const thisKitchen = siteKitchensRes?.kitchens?.find(k => k.kitchen_id === kitchenId);
                                currentSpaces = thisKitchen?.space_list || [];
                                currentMenus = thisKitchen?.menu_list || [];
                            }

                            await axiosHelper.patch(`/kitchen/update/${kitchenId}`, {
                                space_list: [...new Set([...currentSpaces, ...kitchenSpaces])],
                                menu_list: [...new Set([...currentMenus, ...kitchenMenus])],
                            }, { headers });
                        } catch (e) {
                            console.warn(`Failed to update kitchen ${kitchenId}`, e);
                            // We don't necessarily fail the whole thing just because a kitchen link failed,
                            // but for consistency with "atomic" request, we could. 
                            // User asked for "rollback changes if something fails".
                            throw new Error(`Failed to link station to its spaces: ${e.message}`);
                        }
                    }

                    // ====== STEP 3: Fetch Results for UI ======
                    set({ submissionProgress: 'Finalizing...' });
                    const allFinalSpaces = [];
                    for (const sId of affectedSites) {
                        const fParams = new URLSearchParams({ page: 1, PerPage: 100 });
                        const [sRes, qRes, mRes] = await Promise.all([
                            axiosHelper.get(`/v2/site/${sId}/spaces?${fParams.toString()}`, { headers }),
                            axiosHelper.get(`/qr/site/${sId}`, { headers }),
                            axiosHelper.get("/menu/links/a", { headers }),
                        ]);

                        const sData = sRes?.spaces || [];
                        const qData = Array.isArray(qRes) ? qRes : [];
                        const mData = Array.isArray(mRes) ? mRes : [];

                        const finalSpaces = sData.map((space) => {
                            const matchingMenu = mData.find(m => m?.space_id === space?.space_id);
                            const matchingQR = qData.find(qr => qr?.space_id === space?.space_id);

                            return {
                                ...space,
                                id: space.space_id,
                                ...(matchingMenu || {}),
                                qrKey: matchingQR?.key || matchingMenu?.encrypted || null,
                            };
                        });
                        allFinalSpaces.push(...finalSpaces);
                    }

                    const finalRes = { space_info: allFinalSpaces };
                    set({
                        loading: false,
                        submissionProgress: 'Complete!',
                        submissionResult: finalRes,
                    });

                    return { success: true, ...finalRes };
                } catch (error) {
                    console.error('Error submitting spaces, rolling back...', error);

                    set({ submissionProgress: 'Rolling back changes...' });
                    await cleanup();

                    set({
                        error: error.response?.data?.message || error.message || 'Failed to submit spaces',
                        loading: false,
                        submissionProgress: 'Failed',
                    });
                    throw error;
                }
            },


            extractMenu: async (image) => {
                set({ loading: true, error: null });
                const accessToken = await tokenHelper.getToken();
                try {
                    const response = await axiosHelper.post('/ocr/extract-menu', image,
                        {
                            headers: { Authorization: `Bearer ${accessToken}` },
                        }
                    );
                    const extractedText = response.data.data.extractedText;
                    // You might want to auto-populate menu here
                    set({ loading: false });
                    return extractedText;
                } catch (error) {
                    console.error('Error extracting menu:', error);
                    set({ error: error.message, loading: false });
                    throw error;
                }
            },

            reset: () => set({
                currentStep: 1,
                businessType: null,
                selectedSiteId: null,
                siteInfo: null,
                menu: { currency: 'QAR', sections: [] },
                spaces: [],
                experienceMode: 'menu-first',
                loading: false,
                error: null
            }),
        }),
        {
            name: 'experience-storage', // unique name
            partialize: (state) => ({
                // Persist everything except UI state like loading/error
                currentStep: state.currentStep,
                businessType: state.businessType,
                selectedSiteId: state.selectedSiteId,
                plan: state.plan,
                siteInfo: state.siteInfo,
                branding: state.branding,
                menu: state.menu,
                spaces: state.spaces,
                splash: state.splash,
                routing: state.routing,
                experienceMode: state.experienceMode,
                submissionResult: state.submissionResult
            }),
        }
    )
);
